import moment from 'moment-timezone';
import { Device } from '../../clients/mira/types/Device';

export const isAmazonSignageStick = (d: { model: string }) =>
  d.model === 'Amazon Signage Stick';

export type PropertyRow = { label: string; value: string };

// The rows of a device page's Properties table, before the system-on-chip
// block. A row whose value is missing or blank is left out. A Balena box's id
// is its MAC, so a device that is not lite, or is system-on-chip, shows its
// id; any other lite device, such as a Stick, shows the MAC it reported.
export const propertyRows = (
  d: Device,
  { isEnterpriseUser }: { isEnterpriseUser: boolean },
): PropertyRow[] => {
  const isSoC = d.deviceType === 'SoC';
  const serial = d.serialNumber?.trim();
  const mac = !d.isLite || isSoC ? d.id : d.macAddress?.trim();
  const rows: (PropertyRow | false | undefined | '')[] = [
    isEnterpriseUser && {
      label: isSoC ? 'Added By' : 'Owner',
      value: d.resource.profile.name,
    },
    serial && { label: 'Serial Number', value: serial },
    mac && { label: 'MAC address', value: mac },
    {
      label: 'Activated',
      value: d.registeredAt
        ? moment(d.registeredAt).format('MMM Do, YYYY')
        : 'N/A',
    },
  ];
  return rows.filter((r): r is PropertyRow => !!r);
};

// Which Device Management rows a device's page shows. The model is the only
// capability the dashboard can read, so a Stick is told apart by it; every
// other class keeps the gates it had on isLite.
export const deviceManagementRows = (d: { model: string; isLite: boolean }) => {
  const stick = isAmazonSignageStick(d);
  return {
    amazonConsole: false,
    reboot: !d.isLite || stick,
    dailyTimes: !d.isLite || stick,
    // A Stick is lite, so the blackout stays absent: Amazon chooses when a
    // Stick updates.
    blackout: !d.isLite,
    contentCache: stick,
  };
};

type ConnectivityDevice = {
  model: string;
  isLite: boolean;
  deviceType?: string | null;
  metrics?: { connectionType?: string | null } | null;
};

// Whether a device's connection is drawn: the card's mark and the page's
// Connectivity section. A Stick is lite, so it is drawn only once it has
// stored a connection type; before that its page would read "Wireless" with
// no signal and its card would warn of a weak connection.
export const showsConnectivity = (d: ConnectivityDevice): boolean =>
  !d.isLite ||
  d.deviceType === 'SoC' ||
  (isAmazonSignageStick(d) &&
    ['wifi', 'ethernet'].includes(d.metrics?.connectionType ?? ''));

export const WEAK_SIGNAL_STRENGTH = 60;

// signalStrength is getSignalStrength's 0-100 scale, 2 × (rssi + 100), so the
// warning shows below -70 dBm.
export const isSignalStrengthWeak = (
  d: ConnectivityDevice,
  signalStrength: number,
): boolean =>
  showsConnectivity(d) &&
  d.metrics?.connectionType !== 'ethernet' &&
  signalStrength < WEAK_SIGNAL_STRENGTH;
